import { TemplateData } from "../../stores/types/content";
import logger from '../../utils/logger-rum';

export interface StylePacksTabProps {
	setStylePackInfo: (stylePackInfo: StylePackData, id: string) => void;
}

export enum StylePackItemTypes {
	Info = 'info',
	Carousel = 'carousel',
}
export enum StylePackContentType {
	SEGMENT = 'segment',
	TITLE = 'title',
	COUNTDOWN = 'countdown',
	WALKUP = 'walkup',
	BACKGROUND = 'background',
	TRANSITION = 'transition',
	BOOKEND = 'bookend',
	SLIDE = 'slide',
}

const DRAG_TYPE = {
	OVERLAY: 'overlay',
	FEED_MEDIA: 'media',
	FEED_MEDIA_AUDIO: 'audio',
	FEED_PRESENTER: 'presenter',
	FEED_EMPTY: 'empty',
	FEED_EMAIL: 'email',
	FEED_SCREENSHARE: 'screenshare',
	FEED_SIP_CALL: 'sipCall',
	FEED_SRT_STREAM: 'srtStream',
	FEED_ONSITE_LOCATION: 'onsiteLocation',
	TITLE: 'title',
	PROFILE_MASK: 'profileMask',
	// for easier syntax passing into drop area accepted types
	FEEDS: [
		'media',
		'presenter',
		'email',
		'screenshare',
		'title',
		'sipCall',
		'srtStream',
		'onsiteLocation',
	],
};
const templateDragTypes = {
	[StylePackContentType.SEGMENT]:DRAG_TYPE.FEED_MEDIA,
	[StylePackContentType.COUNTDOWN]:DRAG_TYPE.FEED_MEDIA,
	[StylePackContentType.WALKUP]:DRAG_TYPE.FEED_MEDIA,
	[StylePackContentType.BOOKEND]:DRAG_TYPE.FEED_MEDIA,
	[StylePackContentType.BACKGROUND]:DRAG_TYPE.FEED_MEDIA,
	[StylePackContentType.TRANSITION]:DRAG_TYPE.FEED_MEDIA,
	[StylePackContentType.TITLE]:DRAG_TYPE.TITLE,
	[StylePackContentType.SLIDE]:DRAG_TYPE.FEED_MEDIA,
}

export const getTemplateDragType = (type: StylePackContentType | undefined ) => {
	if(!type) {
		logger.error(`Style pack template type ${type} not found. Could be an empty type added.  May need to re-add template with a new or existing type.`);
		return DRAG_TYPE.FEED_MEDIA;
	}
	const templateType = templateDragTypes[type];
	if(!templateDragTypes) {
		logger.error(`Style pack template type ${type} not found. May be a new type. Ask dev to add it to the templateDragTypes map.`);
		throw new Error(`Style pack template type ${type} not found. May be a new type. Ask dev to add it to the templateDragTypes map.`);
	}
	return templateType;
}



export interface ContentTemplate {
	id: number;
	contentType?: StylePackContentType | undefined; // backwards compatibility assume if empty is title
	videoUrl?: string; // backwards compatibility it might not be here
	thumbnailUrl?: string; // backwards compatibility it might not be here
	stylePack: string;
	type: string;
	data?: TemplateData | any;
	provider: string;
	name: string;
	locked: boolean;
	themeId?: number;
	order_index?: number;
}

export interface StylePackGroup {
	type: 'segment' | 'title' | 'countdown' | 'walkup' | 'background' | 'transition' | 'bookend' | 'slide';
	templates: ContentTemplate[];
}

export interface StylePackData {
	id: string;
	name: string;
	locked: boolean;
	stylePack: StylePackGroup[];
	strict?: boolean;
}

export enum STYLE_PACK_FILTER {
	ALL = 'All items',
	BRANDLIVE = 'Brandlive Studios',
};

export const templateLabels: Record<StylePackContentType, string> = {
	[StylePackContentType.SEGMENT]: 'Segments',
	[StylePackContentType.TITLE]: 'Lowers',
	[StylePackContentType.COUNTDOWN]: 'Countdowns',
	[StylePackContentType.WALKUP]: 'Walkups',
	[StylePackContentType.BACKGROUND]: 'Backgrounds',
	[StylePackContentType.TRANSITION]: 'Transitions',
	[StylePackContentType.BOOKEND]: "Bookends",
	[StylePackContentType.SLIDE]: "Slides"
};

export const templateSubLabels: Record<StylePackContentType, string> = {
	[StylePackContentType.SEGMENT]: "",
	[StylePackContentType.TITLE]: "",
	[StylePackContentType.COUNTDOWN]: "",
	[StylePackContentType.WALKUP]: "",
	[StylePackContentType.BACKGROUND]: "",
	[StylePackContentType.TRANSITION]: "",
	[StylePackContentType.BOOKEND]: "Intro & Outro",
	[StylePackContentType.SLIDE]: ""
};

export const mediaTemplates = [
	StylePackContentType.SEGMENT,
	StylePackContentType.COUNTDOWN,
	StylePackContentType.WALKUP,
	StylePackContentType.BOOKEND,
];

//interface for the colloquial name of the pack vs the id of the pack
export enum Style {
	Rothko = 'brandlive-selfridges',
	Topo = 'brandlive-topo',
	BackToSchool = 'brandlive-back-to-school',
	Academy = 'brandlive-academy',
	Mondrian = 'brandlive-mondrian',
	TicTacToe = 'brandlive-tic-tac-toe',
}

export const miscellaneousStylePackTypes = [
	'brandlive-style-pack-20',
	'misc-walkup',
];

export interface StylePackType {
	stylePack: string;
	name: string;
}

export interface ProfileChip {
	type: string // the "id" of the chip to highlight in the chip drop-down
	user: number, // the corresponding user this chip belongs to
	property: string // the presumed property of the user object this chip is using
}


export type FieldType = 'text' | 'number' | 'checkbox' | 'color' | 'select' | 'timer' | 'picture' | 'image' | 'photo' | 'string' | 'duration' | 'float' | 'int' | 'boolean' | 'font';
export type FieldValue = string | number | boolean | null;

export interface FieldOption {
	name: string;
	value: FieldValue;
}

export interface Field {
	hasLabel?: boolean;
	name: string;
	header?: string;
	type: FieldType;
	default: FieldValue;
	validation: string | null;
	required: boolean;
	options: FieldOption[] | null;
	userData: string;
	variables: Array<any>;
	removebg?: boolean;
}


export const InputTypeByCapsuleType: Record<FieldType, FieldType> = {
	image: 'picture',
	picture: 'picture',
	photo: 'picture',

	string: 'text',
	text: 'text',

	float: 'number',
	number: 'number',
	int: 'number',

	select: 'select',

	duration: 'timer',
	timer: 'timer',

	boolean: 'checkbox',
	checkbox: 'checkbox',

	color: 'color',
	font: 'font',
};


export const maxPictureCount = 3;

export enum Chip {
	custom = 'custom',
	full_name = 'full_name',
	first_name = 'first_name',
	last_name = 'last_name',
	title = 'title',
	location = 'location'
}

export interface FieldedChip {
	id: number,
	channel: number,
	generated_content: number,
	data_key: string,
	chip_type: Chip
}

export interface Chipset {
	id: number;
	channel: number;
	generated_content: number;
	gr_user: number;
	chips: FieldedChip[];
	current_profile_version: number;
}

export interface ChipOption {
	type: Chip,
	label: string,
	icon: string,
}

export const ProfileChips: ChipOption[] = [
	{type: Chip.full_name, label: 'Full Name', icon: "presentersUnfilled"},
	{type: Chip.first_name, label: 'First Name', icon: "person"},
	{type: Chip.last_name, label: 'Last Name', icon: "person-unfilled"},
	{type: Chip.title, label: 'Title', icon: "titles"},
	{type: Chip.location, label: 'Location', icon: "sendFilled"},
]

export const CustomChips = [
	{type: Chip.custom, label: 'Custom', icon: "textUnderline"},
]

export const AllChips = [...ProfileChips, ...CustomChips]

export interface HeaderTypes {
	[key: string]: string;
}

export enum HeaderText {
	IntroCopy = 'Intro copy',
	MainCopy = 'Main copy',
	PresenterInfo = 'Presenter info',
	Copy = 'Copy',
}
export const headerTypes:HeaderTypes = {
	image: HeaderText.Copy,
	string: HeaderText.Copy,
	text: HeaderText.Copy,
	duration: HeaderText.Copy,
	color: 'Colors',
	float: 'Numbers',
	int: 'Numbers',
	boolean: 'Toggles',
	select: 'Style',
};